import { isErrorOfVsApi } from '@vs/rtk/utils';

import { referenceOfMeta } from './init';
import {
  AccountGetEntityInfoApiResponse,
  Method as MFAMethod,
  UpdateAccountDto,
  UserMfaGetApiResponse,
  UserMfaListResponseDto,
  vsAccount2ApiInjected,
} from './vsAccount2ApiInjected';

export enum VsAccountSliceTags {
  ACCOUNT_INFO = 'ACCOUNT_INFO',
  AVATAR = 'AVATAR',
  GET_MFA_LIST = 'GET_MFA_LIST',
  USER_SSO_LIST = 'USER_SSO_LIST',
  ACCOUNT_DEFAULT_ENTITY = 'ACCOUNT_DEFAULT_ENTITY',
  ADMIN_PORTAL_ACCOUNT_DETAIL = 'ADMIN_PORTAL_ACCOUNT_DETAIL',
  ADMIN_PORTAL_ACCOUNT_LIST = 'ADMIN_PORTAL_ACCOUNT_LIST',
}

const MFA_LIST = [
  MFAMethod.Authenticator,
  MFAMethod.Email,
  // MFAMethod.Phone,
];

const vsAccount2ApiEnhanced = vsAccount2ApiInjected.enhanceEndpoints({
  addTagTypes: Object.values(VsAccountSliceTags),
  endpoints: {
    accountGetEntityInfo: {
      providesTags: [VsAccountSliceTags.ACCOUNT_DEFAULT_ENTITY],
      extraOptions: {
        isFallbackableError: error =>
          isErrorOfVsApi(error) &&
          error.data?.status_code === 400 &&
          typeof error.data?.message === 'string' &&
          error.data?.message.includes('Cannot find user entity info'),
        fallbackResponse: {
          data: {
            id: '',
            name: '',
            generics_code: '',
            domain_type_code: '',
            area: '',
            create_time: '',
            modify_time: '',
            profile: {
              contact_email: '',
              contact_phone: '',
              zipcode: '',
              country_code: '',
              country: '',
              region: '',
              city: '',
              address: '',
            },
            domains: [],
            entities: [],
          },
        } as AccountGetEntityInfoApiResponse,
      },
    },
    accountFind: {
      providesTags: [VsAccountSliceTags.ACCOUNT_INFO],
      merge(currentCacheData, responseData, otherArgs) {
        return !currentCacheData.modify_time ||
          !responseData.modify_time ||
          new Date(responseData.modify_time) >
            new Date(currentCacheData.modify_time)
          ? responseData
          : currentCacheData;
      },
    },
    accountUpdate: {
      async onQueryStarted(arg, { dispatch, queryFulfilled, getState }) {
        const argsOfQuery = vsAccount2ApiEnhanced.util.selectCachedArgsForQuery(
          getState(),
          'accountFind'
        );
        /**
         * ! Important We sync user account data into indexedDB.
         * ! If user change his login account, there might be several account info data with different account id
         * ! You have to find the current account id to ensure you update the correct one.
         */
        const argOfQuery = argsOfQuery.find(
          e => e.accountId === arg.accountId
        )!;
        const localUpdate = dispatch(
          vsAccount2ApiEnhanced.util.updateQueryData(
            'accountFind',
            argOfQuery,
            draft => {
              const updatePayload =
                arg.updateAccountDto as Required<UpdateAccountDto>;
              if (updatePayload.email !== draft.email) {
                draft.email_is_verified = false;
              }
              Object.assign(draft, updatePayload);
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          localUpdate.undo();
        }
      },
    },
    userAvatarGet: {
      providesTags: [VsAccountSliceTags.AVATAR],
    },
    userAvatarUpdate: {
      invalidatesTags: [VsAccountSliceTags.AVATAR],
    },
    userAvatarDelete: {
      invalidatesTags: [VsAccountSliceTags.AVATAR],
    },
    accountMarketingUpdate: {
      async onQueryStarted(arg, { dispatch, queryFulfilled, getState }) {
        const argsOfQuery = vsAccount2ApiEnhanced.util.selectCachedArgsForQuery(
          getState(),
          'accountMarketingGet'
        );
        /**
         * ! Important We sync user account data into indexedDB.
         * ! If user change his login account, there might be several account info data with different account id
         * ! You have to find the current account id to ensure you update the correct one.
         */
        const argOfQuery = argsOfQuery.find(
          e => e.accountId === arg.accountId
        )!;
        const localUpdate = dispatch(
          vsAccount2ApiEnhanced.util.updateQueryData(
            'accountMarketingGet',
            argOfQuery,
            draft => {
              draft.email_campaign =
                arg.updateUserMarketingCampaignDto.email_campaign;
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          localUpdate.undo();
        }
      },
    },
    accountVerifyEmail: {
      extraOptions: { isPublicEndpoint: true },
      async onQueryStarted(arg, { dispatch, queryFulfilled, getState }) {
        try {
          await queryFulfilled;
          dispatch(
            vsAccount2ApiEnhanced.util.invalidateTags([
              VsAccountSliceTags.ACCOUNT_INFO,
            ])
          );
        } catch {
          //
        }
      },
    },
    accountDeleteUser: {
      extraOptions: { isPublicEndpoint: true },
    },
    userMfaGet: {
      providesTags: [VsAccountSliceTags.GET_MFA_LIST],
      transformResponse: (response: UserMfaGetApiResponse) => {
        const mfaList = MFA_LIST.map(method =>
          response.find(item => item.method === method)
        );
        const availableMfaList = mfaList.filter(
          item => item !== undefined
        ) as UserMfaListResponseDto[];
        return availableMfaList;
      },
    },
    userMfaVerifyOtp: {
      invalidatesTags: [VsAccountSliceTags.GET_MFA_LIST],
    },
    userMfaDelete: {
      invalidatesTags: [VsAccountSliceTags.GET_MFA_LIST],
    },
    userMfaSetDefault: {
      invalidatesTags: [VsAccountSliceTags.GET_MFA_LIST],
      async onQueryStarted(
        { accountId, setMfaAsDefaultDto: { mfaCode } },
        { dispatch, queryFulfilled }
      ) {
        const patchResult = dispatch(
          vsAccount2ApiInjected.util.updateQueryData(
            'userMfaGet',
            { accountId },
            draft => {
              draft.forEach(item => {
                item.isDefault =
                  item.method === (mfaCode as unknown as MFAMethod);
              });
              return draft;
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    },
    userSsoGet: {
      providesTags: [VsAccountSliceTags.USER_SSO_LIST],
      extraOptions: {
        baseUrl: meta => meta._ssoBaseUrl,
      },
    },
    userSsoUpdate: {
      invalidatesTags: [VsAccountSliceTags.USER_SSO_LIST],
      extraOptions: {
        baseUrl: meta => meta._ssoBaseUrl,
      },
    },
    userSsoDelete: {
      extraOptions: {
        baseUrl: meta => meta._ssoBaseUrl,
      },
      async onQueryStarted(
        { accountId, deleteUserSsoDto },
        { dispatch, queryFulfilled }
      ) {
        const patchResult = dispatch(
          vsAccount2ApiInjected.util.updateQueryData(
            'userSsoGet',
            { accountId },
            draft => {
              const deleteTargetIndex = draft.findIndex(
                item => item.sso_code === deleteUserSsoDto.sso_code
              );
              if (deleteTargetIndex !== -1) {
                draft.splice(deleteTargetIndex, 1);
              }
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    },
    userSsoCheck: {
      extraOptions: {
        baseUrl: meta => meta._ssoBaseUrl,
      },
    },
    accountFindDetail: {
      providesTags: [VsAccountSliceTags.ADMIN_PORTAL_ACCOUNT_DETAIL],
    },
    accountFindList: {
      providesTags: [VsAccountSliceTags.ADMIN_PORTAL_ACCOUNT_LIST],
    },
    managementAccountUpdateStatus: {
      invalidatesTags: [
        VsAccountSliceTags.ADMIN_PORTAL_ACCOUNT_DETAIL,
        VsAccountSliceTags.ADMIN_PORTAL_ACCOUNT_LIST,
      ],
    },
    managementAccountDeleteUser: {
      invalidatesTags: [
        VsAccountSliceTags.ADMIN_PORTAL_ACCOUNT_DETAIL,
        VsAccountSliceTags.ADMIN_PORTAL_ACCOUNT_LIST,
      ],
    },
  },
});

export * from './vsAccount2ApiInjected';
export { vsAccount2ApiEnhanced as vsAccount2ApiSlice };
